import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterControls = ({ onFiltersChange, savedFilters, onSaveFilter }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    shiftType: '',
    status: '',
    location: '',
    dateRange: ''
  });
  const [filterName, setFilterName] = useState('');

  const departmentOptions = [
    { value: '', label: 'Alle Abteilungen' },
    { value: 'customer-service', label: 'Kundenservice' },
    { value: 'operations', label: 'Betrieb' },
    { value: 'maintenance', label: 'Wartung' },
    { value: 'security', label: 'Sicherheit' },
    { value: 'administration', label: 'Verwaltung' }
  ];

  const shiftTypeOptions = [
    { value: '', label: 'Alle Schichttypen' },
    { value: 'morning', label: 'Frühschicht' },
    { value: 'afternoon', label: 'Spätschicht' },
    { value: 'night', label: 'Nachtschicht' },
    { value: 'weekend', label: 'Wochenende' }
  ];

  const statusOptions = [
    { value: '', label: 'Alle Status' },
    { value: 'assigned', label: 'Zugewiesen' },
    { value: 'available', label: 'Verfügbar' },
    { value: 'requested', label: 'Angefragt' },
    { value: 'unavailable', label: 'Nicht verfügbar' }
  ];

  const locationOptions = [
    { value: '', label: 'Alle Standorte' },
    { value: 'main-building', label: 'Hauptgebäude' },
    { value: 'warehouse', label: 'Lager' },
    { value: 'reception', label: 'Empfang' },
    { value: 'parking', label: 'Parkplatz' }
  ];

  const dateRangeOptions = [
    { value: '', label: 'Alle Termine' },
    { value: 'today', label: 'Heute' },
    { value: 'week', label: 'Diese Woche' },
    { value: 'month', label: 'Dieser Monat' },
    { value: 'quarter', label: 'Dieses Quartal' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      department: '',
      shiftType: '',
      status: '',
      location: '',
      dateRange: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const handleSaveFilter = () => {
    if (filterName?.trim()) {
      onSaveFilter(filterName, filters);
      setFilterName('');
    }
  };

  const handleLoadSavedFilter = (savedFilter) => {
    setFilters(savedFilter?.filters);
    onFiltersChange(savedFilter?.filters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card border border-border rounded-lg mb-4">
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Filter</h3>
          {hasActiveFilters && (
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
              Aktiv
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              iconName="X"
              iconSize={14}
            >
              Zurücksetzen
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconSize={16}
          >
            {isExpanded ? 'Weniger' : 'Mehr'}
          </Button>
        </div>
      </div>
      {/* Quick Filters */}
      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            placeholder="Status"
          />

          <Select
            options={departmentOptions}
            value={filters?.department}
            onChange={(value) => handleFilterChange('department', value)}
            placeholder="Abteilung"
          />

          <Select
            options={shiftTypeOptions}
            value={filters?.shiftType}
            onChange={(value) => handleFilterChange('shiftType', value)}
            placeholder="Schichttyp"
          />

          <Select
            options={locationOptions}
            value={filters?.location}
            onChange={(value) => handleFilterChange('location', value)}
            placeholder="Standort"
          />

          <Select
            options={dateRangeOptions}
            value={filters?.dateRange}
            onChange={(value) => handleFilterChange('dateRange', value)}
            placeholder="Zeitraum"
          />
        </div>
      </div>
      {/* Expanded Filters */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="pt-4 space-y-4">
            {/* Saved Filters */}
            {savedFilters?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Gespeicherte Filter
                </label>
                <div className="flex flex-wrap gap-2">
                  {savedFilters?.map((savedFilter) => (
                    <Button
                      key={savedFilter?.id}
                      variant="outline"
                      size="sm"
                      onClick={() => handleLoadSavedFilter(savedFilter)}
                      iconName="Bookmark"
                      iconSize={14}
                    >
                      {savedFilter?.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Save Current Filter */}
            {hasActiveFilters && (
              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <Input
                    label="Filter speichern"
                    type="text"
                    placeholder="Filter-Name eingeben"
                    value={filterName}
                    onChange={(e) => setFilterName(e?.target?.value)}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={handleSaveFilter}
                  disabled={!filterName?.trim()}
                  iconName="Save"
                  iconSize={16}
                >
                  Speichern
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;